import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, Loader2, Package, User, Building2, Calendar, 
  DollarSign, Truck, FileText, AlertCircle, CheckCircle, 
  XCircle, Clock, RefreshCw, ClipboardList, ScrollText
} from 'lucide-react';
import axios from 'axios';
import RecordAuditTrail from '../components/audit/RecordAuditTrail';
import WorkflowActivity from '../components/workflow/WorkflowActivity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SalesOrderView = () => {
  const navigate = useNavigate();
  const { salesOrderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [documentFlow, setDocumentFlow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [viewTab, setViewTab] = useState('overview');


  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [orderRes, flowRes, custRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/otc/sales-orders/${salesOrderNumber}`),
        axios.get(`${BACKEND_URL}/api/otc/sales-orders/${salesOrderNumber}/document-flow`),
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] }))
      ]);
      setOrder(orderRes.data);
      setDocumentFlow(flowRes.data);
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error('Error loading sales order:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load sales order: ${errorMsg}`);
      navigate('/otc/sales-orders');
    } finally {
      setIsLoading(false);
    }
  }, [salesOrderNumber, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customerId;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'OPEN': return 'bg-orange-100 text-orange-800';
      case 'IN_PROCESS': return 'bg-yellow-100 text-yellow-800';
      case 'PARTIALLY_DELIVERED': return 'bg-orange-100 text-orange-800';
      case 'DELIVERED': return 'bg-green-100 text-green-800';
      case 'COMPLETED': return 'bg-emerald-100 text-emerald-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED': case 'DELIVERED': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'CANCELLED': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'IN_PROCESS': case 'PARTIALLY_DELIVERED': return <Clock className="w-5 h-5 text-yellow-600" />;
      default: return <FileText className="w-5 h-5 text-orange-600" />;
    }
  };

  const handleCreateDelivery = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/otc/deliveries/from-sales-order/${salesOrderNumber}`);
      toast.success(`Delivery '${response.data.delivery_number}' created successfully!`);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error creating delivery:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create delivery: ${errorMsg}`);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading sales order...</span>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-screen">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-gray-600">Sales order not found</p>
          <Button onClick={() => navigate('/otc/sales-orders')} className="mt-4">
            Back to Sales Orders
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/sales-orders')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Sales Order {order.sales_order_number}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(order.status)}`}>
                  {getStatusIcon(order.status)}
                  {order.status?.replace('_', ' ')}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Document Date: {order.document_date?.split('T')[0]} | Customer: {getCustomerName(order.sold_to_party)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {['OPEN', 'IN_PROCESS', 'PARTIALLY_DELIVERED'].includes(order.status) && (
              <Button onClick={handleCreateDelivery} data-testid="create-delivery-btn">
                <Truck className="w-4 h-4 mr-2" />
                Create Delivery
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-1 border-b border-gray-200 mb-6">
          <button
            onClick={() => setViewTab('overview')}
            data-testid="so-tab-overview"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${viewTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setViewTab('audit')}
            data-testid="so-tab-audit"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${viewTab === 'audit' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <ScrollText className="w-4 h-4" /> Audit Trail
          </button>
        </div>

        {viewTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Data */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Header Data
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Document Type</p>
                  <p className="font-medium">{order.document_type}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sales Organization</p>
                  <p className="font-medium">{order.sales_organization}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Distribution Channel</p>
                  <p className="font-medium">{order.distribution_channel}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Division</p>
                  <p className="font-medium">{order.division || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pricing Procedure</p>
                  <p className="font-medium">{order.pricing_procedure}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Currency</p>
                  <p className="font-medium">{order.currency}</p>
                </div>
              </div>
            </Card>

            {/* Customer/Partner Data */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Partner Functions
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Sold-to Party</p>
                  <p className="font-medium">{order.sold_to_party}</p>
                  <p className="text-xs text-gray-600">{getCustomerName(order.sold_to_party)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ship-to Party</p>
                  <p className="font-medium">{order.ship_to_party}</p>
                  <p className="text-xs text-gray-600">{getCustomerName(order.ship_to_party)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bill-to Party</p>
                  <p className="font-medium">{order.bill_to_party}</p>
                  <p className="text-xs text-gray-600">{getCustomerName(order.bill_to_party)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Payer</p>
                  <p className="font-medium">{order.payer}</p>
                  <p className="text-xs text-gray-600">{getCustomerName(order.payer)}</p>
                </div>
              </div>
            </Card>

            {/* Items */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Items ({order.items?.length || 0})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="so-items-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-2 px-3 text-sm font-semibold">Item</th>
                      <th className="text-left py-2 px-3 text-sm font-semibold">Material</th>
                      <th className="text-left py-2 px-3 text-sm font-semibold">Description</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Qty</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Delivered</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Net Price</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Gross Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((item, index) => (
                      <tr key={`item-${item.item_number || index}`} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-mono">{item.item_number?.toString().padStart(4, '0')}</td>
                        <td className="py-2 px-3">{item.material_id || '-'}</td>
                        <td className="py-2 px-3">{item.item_description}</td>
                        <td className="py-2 px-3 text-right">
                          {item.order_quantity} {item.unit_of_measure}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {item.delivered_quantity || 0} {item.unit_of_measure}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {item.currency} {item.net_price?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold">
                          {item.currency} {item.gross_value?.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Totals */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Order Totals
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Net Value:</span>
                  <span className="font-mono">{order.currency} {order.net_value?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax Amount:</span>
                  <span className="font-mono text-red-600">+ {order.currency} {order.tax_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-gray-200">
                  <span className="font-semibold">Gross Value:</span>
                  <span className="font-mono font-bold text-lg">{order.currency} {order.gross_value?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              </div>
            </Card>

            {/* Dates & Terms */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Dates & Terms
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Document Date</p>
                  <p className="font-medium">{order.document_date?.split('T')[0]}</p>
                </div>
                {order.requested_delivery_date && (
                  <div>
                    <p className="text-xs text-gray-500">Requested Delivery</p>
                    <p className="font-medium">{order.requested_delivery_date?.split('T')[0]}</p>
                  </div>
                )}
                {order.customer_reference && (
                  <div>
                    <p className="text-xs text-gray-500">Customer Reference</p>
                    <p className="font-medium">{order.customer_reference}</p>
                  </div>
                )}
                {order.payment_terms && (
                  <div>
                    <p className="text-xs text-gray-500">Payment Terms</p>
                    <p className="font-medium">{order.payment_terms}</p>
                  </div>
                )}
                {order.incoterms && (
                  <div>
                    <p className="text-xs text-gray-500">Incoterms</p>
                    <p className="font-medium">{order.incoterms} {order.incoterms_location}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Document Flow */}
            {documentFlow && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Document Flow
                </h3>
                <div className="space-y-4">
                  {/* Sales Order */}
                  <div className="flex items-center gap-3 p-2 bg-orange-50 rounded-lg">
                    <FileText className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium text-orange-800">Sales Order</p>
                      <p className="text-xs text-orange-600">{order.sales_order_number}</p>
                    </div>
                  </div>

                  {/* Deliveries */}
                  {documentFlow.deliveries && documentFlow.deliveries.length > 0 ? (
                    documentFlow.deliveries.map((del) => (
                      <div key={del.delivery_number} className="flex items-center gap-3 p-2 bg-green-50 rounded-lg ml-4">
                        <Truck className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">Delivery</p>
                          <p className="text-xs text-green-600">{del.delivery_number} - {del.status}</p>
                          {del.picking_status && del.picking_status !== 'NOT_STARTED' && (
                            <p className="text-xs text-amber-600">Picking: {del.picking_status}</p>
                          )}
                          {del.packing_status && del.packing_status !== 'NOT_STARTED' && (
                            <p className="text-xs text-orange-600">Packing: {del.packing_status}</p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500 ml-4">No deliveries yet</div>
                  )}

                  {/* Pick Lists */}
                  {documentFlow.pick_lists && documentFlow.pick_lists.length > 0 && (
                    documentFlow.pick_lists.map((pl) => (
                      <div key={pl.pick_list_number} className="flex items-center gap-3 p-2 bg-amber-50 rounded-lg ml-6">
                        <ClipboardList className="w-5 h-5 text-amber-600" />
                        <div>
                          <p className="font-medium text-amber-800">Pick List</p>
                          <p className="text-xs text-amber-600">{pl.pick_list_number} - {pl.status}</p>
                        </div>
                      </div>
                    ))
                  )}

                  {/* Handling Units */}
                  {documentFlow.handling_units && documentFlow.handling_units.length > 0 && (
                    documentFlow.handling_units.map((hu) => (
                      <div key={hu.hu_number} className="flex items-center gap-3 p-2 bg-orange-50 rounded-lg ml-6">
                        <Package className="w-5 h-5 text-orange-600" />
                        <div>
                          <p className="font-medium text-orange-800">Handling Unit</p>
                          <p className="text-xs text-orange-600">{hu.hu_number} - {hu.hu_type}</p>
                        </div>
                      </div>
                    ))
                  )}

                  {/* Billing Documents */}
                  {documentFlow.billing_documents && documentFlow.billing_documents.length > 0 ? (
                    documentFlow.billing_documents.map((bill) => (
                      <div key={bill.billing_number} className="flex items-center gap-3 p-2 bg-orange-50 rounded-lg ml-8">
                        <DollarSign className="w-5 h-5 text-orange-600" />
                        <div>
                          <p className="font-medium text-orange-800">Invoice</p>
                          <p className="text-xs text-orange-600">{bill.billing_number} - {bill.status}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500 ml-8">No invoices yet</div>
                  )}
                </div>
              </Card>
            )}

            {/* Notes */}
            {order.header_note && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Notes</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{order.header_note}</p>
              </Card>
            )}
          </div>
        </div>
        )}

        {viewTab === 'audit' && (
          <div className="space-y-6">
            <WorkflowActivity doctype="sales_order" recordId={order.sales_order_number} currentState={order.status} />
            <RecordAuditTrail doctype="sales_order" recordId={order.sales_order_number} />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SalesOrderView;
